import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../../lib/LayoutContainers/DashboardLayout";
import styles from "./MetabaseDashboard.module.css";
import { post } from "../../requests";
import withDommer from "../withDommer"; // ← the identical CSS module

declare global {
  interface Window {
    keycloak?: { token?: string };
  }
}
const getKeycloak = () => (typeof window !== "undefined" ? window.keycloak! : { token: "" });

interface DashboardEntry {
  embedUrl: string;
  dashboardId: number;
  name: string;
  is_synd_dash: boolean;
}

const MetabaseViewerWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [entry, setEntry] = useState<DashboardEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const abortRef = useRef<AbortController>();

  useEffect(() => {
    setEntry(null);
    setError("");
    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    (async () => {
      try {
        const roleName = localStorage.getItem("userRole") || "";
        const allowedSyndicatorRoles = ["syndicator", "syndicator(limited)"];
        const isSyndicator = allowedSyndicatorRoles.includes(roleName.toLowerCase());
        const userId = localStorage.getItem("logInUserId");

        let apiUrl = "get-admin-dashboard-urls";
        let apiBody: Record<string, any> = { filters: {} };

        if (isSyndicator) {
          apiUrl = "get-syndicator-own-dashboard-urls";
          if (!userId) {
            throw new Error("Syndicator user ID not found. Cannot load dashboard.");
          }
          apiBody = { userId: parseInt(userId, 10) };
        }

        const all: DashboardEntry[] = await post(apiUrl, apiBody);
        const found = all.find((d) => d.dashboardId.toString() == id);

        if (!found) {
          throw new Error("Dashboard not found for this tenant.");
        }
        if (found.is_synd_dash && !isSyndicator) {
          throw new Error(
            "ACCESS_DENIED: You do not have permission to view this syndicator dashboard."
          );
        }
        if (!found.is_synd_dash && isSyndicator) {
          throw new Error(
            "ACCESS_DENIED: You do not have permission to view this admin dashboard."
          );
        }

        setEntry(found);
      } catch (e: any) {
        if (!ctrl.signal.aborted) setError(e.message);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      ctrl.abort();
    };
  }, [id]);

  return (
    <DashboardLayout>
      <div className={styles.container}>
        {loading && !error && <div className={styles.loadingContainer}>Loading dashboard…</div>}
        {!!error && <div className={styles.errorContainer}>{error}</div>}
        {entry && (
          <iframe
            key={entry.dashboardId}
            src={entry.embedUrl}
            title={entry.name}
            className={styles.iframe}
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-downloads allow-forms"
            onError={() => setError("Error loading dashboard")}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default withDommer(MetabaseViewerWrapper);
